import {
    getOrganizationId,
    getSalesforceMessagingUrl,
    getJwt,
    getLastEventId,
    storeSalesforceMessagingUrl
} from './dataProvider';
import { EventSourcePolyfill } from 'event-source-polyfill';

/**
 * Build SSE URL based on environment.
 * In development mode, returns relative paths to leverage setupProxy.js
 * In production/staging, returns full URLs with Salesforce domain
 *
 * @param {String} path - SSE path like "/eventrouter/v1/sse"
 * @returns {String} - Full URL or relative path depending on environment
 */
function buildSseUrl(path) {
    const isDevelopment = process.env.NODE_ENV === 'development';

    if (isDevelopment) {
        // Development: Use relative path for proxy
        // setupProxy.js will intercept and forward to Salesforce
        return path;
    } else {
        // Production/Staging: Use full URL
        // CORS must be configured in Salesforce to allow your domain
        const messagingUrl = getSalesforceMessagingUrl();
        return `${messagingUrl}${path}`;
    }
}

/**
 * Instance of an EventSourcePolyfill object to add or remove event listeners on.
 */
let eventSource;

/**
 * Get the request headers for connecting to SSE.
 */
const getEventSourceParams = () => {
    const jwt = getJwt();
    const orgId = getOrganizationId();
    const lastEventId = getLastEventId() ?? "";
    if (!jwt || !orgId) {
        throw new Error(`[SSE] Missing JWT or Org ID. JWT: ${jwt}, OrgId: ${orgId}`);
    }

    let request = {
        headers: {
            "Authorization": "Bearer " + jwt,
            "X-Org-ID": orgId,
        },
        heartbeatTimeout: 90000
    };

    if(!lastEventId) {
        request.headers.lastEventId = lastEventId;
    }

    return request;
};

/**
 * Add event listeners on an EventSourcePolyfill object.
 *
 * @param {String} listenerOperationName - Name of the operator to act on an instance of EventSourcePolyfill, either "addEventListener" or "removeEventListener".
 * @param {Object} eventListenerMap - Map of event names and corresponding handlers for server-sent events. Shape of object is { eventName: eventHandler }.
 */
const handleEventSourceListeners = (eventSource, listenerOperationName, eventListenerMap) => {
    if (typeof eventSource !== "object" || eventSource === null) {
        throw new Error(`Expected eventSource to be a valid object, but received: ${eventSource}`);
    }
    if (typeof listenerOperationName !== "string" || !listenerOperationName.length) {
        throw new Error(`Expected a valid string to either add or remove event listeners, but received: ${listenerOperationName}`);
    }

    Object.entries(eventListenerMap).forEach(([eventName, eventHandler]) => {
        if (typeof eventName !== "string" || eventName.trim().length === 0) {
            throw new Error(`Invalid event name: ${eventName}`);
        }
        if (typeof eventHandler !== "function") {
            throw new Error(`Invalid event handler for ${eventName}: ${eventHandler}`);
        }

        eventSource[listenerOperationName](eventName, eventHandler);
    });
};

/**
 * Establish the EventSource object with handlers for onopen and onerror.
 *
 * @param {String} fullApiPath - The full API path endpoint to request server-sent events.
 * @param {Object} eventListenerMap - Map of event handlers for server-sent events. Shape of object is { eventName: eventHandler }.
 * @returns {Promise}
 */
export const createEventSource = (fullApiPath, eventListenerMap) => {
    if (typeof fullApiPath !== "string" || !fullApiPath.length) {
        throw new Error(`Expected full API path parameter to be a valid string, but received: ${fullApiPath}.`);
    }
    if (typeof eventListenerMap !== "object") {
        throw new Error(`Expected event listener map parameter to be a valid object map, but received: ${eventListenerMap}.`);
    }

    /**
     * Create a closure here to isolate `reconnectAttempts` and `reconnectIntervalSeconds` values to a single invocation of `createEventSource`.
     * All calls to `resolveEventSource` within a call to `createEventSource` share the same `reconnectAttempts` and `reconnectIntervalSeconds` values.
     *
     * @param {Promise.resolve} resolve - Event source opened.
     * @param {Promise.reject} reject - Attempted to reconnect to event source too many times.
     */
    const resolveEventSource = (resolve, reject) => {
        try {
            if (eventSource) {
                eventSource.close();
            }

            eventSource = new EventSourcePolyfill(fullApiPath, getEventSourceParams());


            eventSource.onopen = () => {
                handleEventSourceListeners(eventSource, "addEventListener", eventListenerMap);
                resolve(eventSource);
            };

            eventSource.onerror = (error) => {
                reject(error);
            };
        } catch (err) {
            reject(err);
        }

    };

    return new Promise(resolveEventSource);
};

/**
 * Subscribe to EventSource by providing authorization and org details.
 *
 * @param {Object} eventListenerMap - Map of event handlers for server-sent events. Shape of object is { eventName: eventHandler }.
 * @returns {Promise}
 */
export const subscribeToEventSource = (eventListenerMap) => {
    const messagingUrl = getSalesforceMessagingUrl();

    if (!messagingUrl) {
        return Promise.reject(new Error(`Expected a valid Messaging URL to establish a connection to the Event Source, but instead received ${messagingUrl}`));
    }
    return new Promise((resolve, reject) => {
        try {
            /**
             * Directly connect to event router endpoint on Salesforce Messaging domain instead of going through ia-message.
             */
            createEventSource(
                buildSseUrl(`/eventrouter/v1/sse?_ts=${Date.now()}`),
                eventListenerMap
            ).then(
                resolve,
                error => {
                    /**
                     * If this reject function is called, there are likely three possibilities:
                     * 1. a syntactic error in creating the EventSource
                     * 2. too many reconnect attempts
                     * 3. JWT has expired
                     */
                    reject(error);
                }
            ).catch(error => {
                reject(error);
            });
        } catch (error) {
            reject(error);
        }
    });
};

/**
 * Close the event source to end event stream.
 * @returns {Promise}
 */
export const closeEventSource = () => {
    return new Promise((resolve, reject) => {
        try {
            if (eventSource) {
                eventSource.close();
            }
            resolve();
        } catch (error) {
            reject(error);
        }
    });
};